import { CheckCircleIcon, CircleAlertIcon, CopyIcon, Loader2, TriangleAlertIcon } from 'lucide-react'
import { useRef, useState } from 'react'

import { adminQuotingProspects } from 'rails_routes'
import { Banner, Button } from 'slab'
import { useGetQuotingSmartCensusParseJobsId } from 'utils/web_api'
import type { QuotingSmartCensusParseJobStatus } from 'utils/web_api.schemas'

import DefineEmployeeRelationshipValuesView from './DefineEmployeeRelationshipValuesView'
import InputOutputComparisonTable from './InputOutputComparisonTable'

const POLLING_INTERVAL_MS = 3000

const flashPageTitle = (
  title: string,
  options: {
    durationMs?: number,
    intervalMs?: number,
    clearOnVisibilityChange?: boolean
  } = {}
) => {
  const { durationMs, intervalMs = 2000, clearOnVisibilityChange = true } = options
  const ogTitle = document.title
  let showingFlashTitle = true
  document.title = title

  const intervalId = setInterval(() => {
    showingFlashTitle = !showingFlashTitle
    document.title = showingFlashTitle ? title : ogTitle
  }, intervalMs)

  const cleanup = () => {
    clearInterval(intervalId)
    document.title = ogTitle
  }

  if (durationMs) {
    setTimeout(cleanup, durationMs)
  }

  if (clearOnVisibilityChange) {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        cleanup()
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }
}

export default function SmartCensusParseJobDetails ({ jobId }: { jobId: string }) {
  const [quotingUrlCopied, setQuotingUrlCopied] = useState(false)
  const prevStatusRef = useRef<QuotingSmartCensusParseJobStatus | null>(null)

  const { data: job, isLoading, error } = useGetQuotingSmartCensusParseJobsId(jobId, {
    query: {
      refetchInterval: (query: any) => {
        const currentStatus = query.state.data?.status
        const prevStatus = prevStatusRef.current

        // Flash title on status transitions (not on initial load)
        if (currentStatus === 'completed' && prevStatus === 'in_progress') {
          flashPageTitle('CADE | Processing complete! ✅', {
            clearOnVisibilityChange: true,
          })
        } else if (currentStatus === 'in_progress') {
          flashPageTitle('CADE | Processing census ⏳', {
            clearOnVisibilityChange: false,
          })
        }
        prevStatusRef.current = currentStatus

        // Continue polling if the job is still in progress
        return currentStatus === 'in_progress' ? POLLING_INTERVAL_MS : false
      },
      retry: (failureCount, error) => {
        return ((error as any)?.status === 404) ? false : failureCount < 3
      },
    },
  })

  const copyQuotingUrl = () => {
    const quotingUrl = `${window.location.origin}${adminQuotingProspects.new({ query: { census_id: job?.quoting_prospect_employee_census_id || '' } })}`
    navigator.clipboard.writeText(quotingUrl)
    setQuotingUrlCopied(true)
    setTimeout(() => {
      setQuotingUrlCopied(false)
    }, 2000)
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <Loader2 className='h-8 w-8 animate-spin text-blue-500' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='p-4 text-red-600'>
        Error loading job: {(error as any)?.message || 'Unknown error'}
      </div>
    )
  }

  if (!job) {
    return (
      <div className='p-4 text-red-600'>
        Job not found
      </div>
    )
  }

  return (
    <div className='p-4 space-y-4'>
      {job.status === 'in_progress' && (
        <div className='p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2'>
          <div className='flex items-center gap-3'>
            <Loader2 className='h-4 w-4 animate-spin text-blue-500' />
            <p className='font-medium text-blue-900'>
              {job.num_formatted_rows_to_process ? 'Re-mapping census...' : 'Reading census...'}
            </p>
          </div>
          <div className='h-2 bg-gray-200 rounded-lg overflow-hidden w-full'>
            <div
              className='h-full bg-thatch-500 transition-all duration-300'
              style={{ width: `${job.num_formatted_rows_to_process ? (job.num_output_rows_processed / job.num_formatted_rows_to_process) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {job.status === 'awaiting_relationship_definition' && (
        <DefineEmployeeRelationshipValuesView job={job} />
      )}

      {job.status === 'completed' && (
        <div className='bg-green-50 border border-green-200 rounded-lg p-4 space-y-2'>
          <div className='flex items-center gap-3'>
            <CheckCircleIcon size={16} className='text-green-400' />
            <p className='font-medium text-green-900'>Processing Complete!</p>
          </div>
          <div className='flex items-center gap-4 justify-between'>
            <p>Successfully re-mapped census into <strong>{job.num_output_rows_processed} Prospect Employees</strong></p>
            <div className='flex justify-end mt-2'>
              {job.quoting_prospect_employee_census_id && (
                <Button
                  variant='secondary'
                  rightIcon={quotingUrlCopied ? CheckCircleIcon : CopyIcon}
                  onClick={copyQuotingUrl}
                >
                  Copy link to create new Prospect
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {['completed', 'failed'].includes(job.status) && job.parsing_errors.length > 0 && (
        <Banner type='critical' title='Parsing Errors' icon={CircleAlertIcon}>
          <ul className='list-disc list-inside space-y-1 max-h-44 overflow-y-scroll'>
            {job.parsing_errors.map((error: string, index: number) => (
              <li key={index} className='text-sm text-red-700'>
                {error}
              </li>
            ))}
          </ul>
        </Banner>
      )}

      {['completed', 'failed'].includes(job.status) && job.parsing_warnings.length > 0 && (
        <Banner type='caution' title={`${job.parsing_warnings.length} Parsing Warnings`} icon={TriangleAlertIcon}>
          <ul className='list-disc list-inside space-y-1 max-h-44 overflow-y-auto scrollbar scrollbar-thumb-gray-400 scrollbar-track-gray-200'>
            {job.parsing_warnings.map((warning: string, index: number) => (
              <li key={index} className='text-sm text-yellow-700'>
                {warning}
              </li>
            ))}
          </ul>
        </Banner>
      )}

      {job.status !== 'awaiting_relationship_definition' && (
        <InputOutputComparisonTable jobId={jobId} status={job.status} />
      )}
    </div>
  )
}
